import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Tag, Clock, Copy, Check, Percent, DollarSign, Gift, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { PageHero } from '@/components/common/PageHero';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { useSiteContent } from '@/context/SiteContentContext';
import { usePageDesign } from '@/hooks/usePageDesign';

interface Promotion {
  id: string;
  title: string;
  description: string | null;
  discount_type: string;
  discount_value: number;
  promo_code: string | null;
  is_active: boolean | null;
  start_date: string | null;
  end_date: string | null;
  display_banner: boolean | null;
  banner_text: string | null;
  created_at: string;
}

const formatDiscount = (type: string, value: number) => {
  if (type === 'percentage') return `${value}% OFF`;
  if (type === 'fixed') return `$${value} OFF`;
  return `${value} OFF`;
};

const DiscountIcon: React.FC<{ type: string }> = ({ type }) => {
  if (type === 'percentage') return <Percent className="h-6 w-6" />;
  if (type === 'fixed') return <DollarSign className="h-6 w-6" />;
  return <Gift className="h-6 w-6" />;
};

function PromoCopyButton({ code }: { code: string }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 text-xs font-mono bg-muted px-3 py-1.5 rounded-md border border-border hover:bg-muted/80 transition-colors"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
      {code}
    </button>
  );
}

export default function OffersPage() {
  const siteContent = useSiteContent();
  const companyName = siteContent.template?.brand?.name || 'Our Company';
  const { heroStyle, showBreadcrumbs, showTrustBadges, showCtaButtons } = usePageDesign('promotions');

  const { data: promotions = [], isLoading } = useQuery({
    queryKey: ['public-promotions'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('promotions')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as Promotion[];
    },
    staleTime: 1000 * 60 * 5,
  });

  const activePromos = promotions.filter(p => {
    if (p.end_date && new Date(p.end_date) < new Date()) return false;
    return true;
  });

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <>
      <SEOHead
        title={`Promos & Special Deals | ${companyName}`}
        description={`Browse current promos and deals from ${companyName}. Save on services with our limited-time promotions.`}
      />

      <ConfigurableHero
        heroStyle={heroStyle}
        title="Current Promos"
        subtitle="Take advantage of our current promotions and save on quality services."
        badge="Limited Time"
        showBreadcrumbs={showBreadcrumbs}
        breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Promos' }]}
        showTrustBadges={showTrustBadges}
        showCtaButtons={showCtaButtons}
      />

      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : activePromos.length === 0 ? (
            <div className="text-center py-20">
              <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">No active offers right now</h2>
              <p className="text-muted-foreground mb-6">Check back soon — we regularly post new promotions and deals.</p>
              <Link to="/contact">
                <Button>Contact Us for a Quote</Button>
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activePromos.map((promo, i) => (
                <motion.div
                  key={promo.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.4 }}
                >
                  <Card className="h-full flex flex-col overflow-hidden group hover:shadow-lg transition-shadow">
                    {/* Discount banner */}
                    <div className="bg-primary/10 p-6 flex items-center gap-4">
                      <div className="bg-primary/20 rounded-xl p-3 text-primary">
                        <DiscountIcon type={promo.discount_type} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-primary">
                          {formatDiscount(promo.discount_type, promo.discount_value)}
                        </p>
                        {promo.end_date && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Clock className="h-3 w-3" />
                            Expires {formatDate(promo.end_date)}
                          </p>
                        )}
                      </div>
                    </div>

                    <CardContent className="p-6 flex flex-col flex-1">
                      <h3 className="font-heading font-semibold text-lg mb-2">{promo.title}</h3>
                      {promo.description && (
                        <p className="text-sm text-muted-foreground mb-4 flex-1">{promo.description}</p>
                      )}

                      <div className="mt-auto space-y-3">
                        {promo.promo_code && (
                          <div className="flex items-center gap-2">
                            <Tag className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Promo code:</span>
                            <PromoCopyButton code={promo.promo_code} />
                          </div>
                        )}

                        {promo.start_date && (
                          <Badge variant="outline" className="text-xs">
                            Valid from {formatDate(promo.start_date)}
                          </Badge>
                        )}

                        <Link to="/contact" className="block">
                          <Button className="w-full">
                            {promo.promo_code ? 'Schedule Service' : 'Claim This Offer'}
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-muted/40">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading text-2xl lg:text-3xl font-bold mb-4">Need a Custom Quote?</h2>
          <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
            Can't find what you're looking for? Contact us for a personalized estimate.
          </p>
          <Link to="/contact">
            <Button size="lg" variant="default">Get a Free Quote</Button>
          </Link>
        </div>
      </section>
    </>
  );
}
